import type { PatientDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { Command, CommandInput, CommandList } from "@repo/ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { patientDisplayName } from "@/shared/lib/patient-utils";
import { PatientSearchList } from "@/shared/pickers/patient-search-list";

export interface PatientPickerProps {
  value: string | undefined;
  onChange: (patientId: string) => void;
  onBlur?: () => void;
  patients: PatientDto[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

/** Controlled patient combobox (Popover + Command). Form-agnostic — see PatientField for the form-bound wrapper. */
export function PatientPicker({
  value,
  onChange,
  onBlur,
  patients,
  placeholder = "Select patient...",
  disabled,
  isLoading,
  className,
}: PatientPickerProps) {
  const [open, setOpen] = useState(false);

  const selectedPatient = useMemo(() => {
    if (!value) return null;
    return patients.find((p) => p.patientId === value) ?? null;
  }, [patients, value]);

  const selectedName = patientDisplayName(selectedPatient) ?? null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) onBlur?.();
      }}
    >
      <PopoverTrigger
        render={<Button variant="outline" className={`w-full justify-between ${className ?? ""}`} />}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <span className="text-muted-foreground">Loading...</span>
        ) : selectedName ? (
          <span>{selectedName}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-(--anchor-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search patients..." />
          <CommandList>
            <PatientSearchList
              patients={patients}
              onSelect={(patientId) => {
                onChange(patientId);
                setOpen(false);
              }}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
